import { useState, useEffect, useCallback, useRef } from 'react';
import { AdyenCheckout, Card } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import {
  validateAdyenClientKey,
  getAdyenClientKeySetupError,
  isIgnorableAdyenError,
  cardOnlyAdyenPaymentMethods,
} from '../utils/adyenStyleMapper';

/**
 * Adyen Web v6 Card component — https://docs.adyen.com/payment-methods/cards/web-component
 *
 * @param {boolean} config.enabled - When false, no SDK init (wait for clientKey + paymentMethods)
 */
export const useAdyenCard = ({
  enabled = false,
  clientKey,
  environment = 'test',
  paymentMethodsResponse,
  locale = 'en-US',
  countryCode = 'US',
  styles = {},
  cardConfiguration = {},
  onReady,
  onConfigSuccess,
  onError,
  onBrand,
  onChange,
  onAdditionalDetails,
  onPaymentCompleted,
  onPaymentFailed,
}) => {
  const [checkoutInstance, setCheckoutInstance] = useState(null);
  const [cardComponent, setCardComponent] = useState(null);
  const [ready, setReady] = useState(false);
  const [configReady, setConfigReady] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cardMetadata, setCardMetadata] = useState(null);

  const checkoutRef = useRef(null);
  const cardRef = useRef(null);
  const mountedRef = useRef(false);
  const submitResolverRef = useRef(null);

  const onReadyRef = useRef(onReady);
  const onConfigSuccessRef = useRef(onConfigSuccess);
  const onErrorRef = useRef(onError);
  const onBrandRef = useRef(onBrand);
  const onChangeRef = useRef(onChange);
  const onAdditionalDetailsRef = useRef(onAdditionalDetails);
  const onPaymentCompletedRef = useRef(onPaymentCompleted);
  const onPaymentFailedRef = useRef(onPaymentFailed);
  const actionComponentRef = useRef(null);

  useEffect(() => {
    onReadyRef.current = onReady;
    onConfigSuccessRef.current = onConfigSuccess;
    onErrorRef.current = onError;
    onBrandRef.current = onBrand;
    onChangeRef.current = onChange;
    onAdditionalDetailsRef.current = onAdditionalDetails;
    onPaymentCompletedRef.current = onPaymentCompleted;
    onPaymentFailedRef.current = onPaymentFailed;
  }, [
    onReady,
    onConfigSuccess,
    onError,
    onBrand,
    onChange,
    onAdditionalDetails,
    onPaymentCompleted,
    onPaymentFailed,
  ]);

  const resolveSubmit = useCallback((state) => {
    if (submitResolverRef.current) {
      submitResolverRef.current(state);
      submitResolverRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    if (!clientKey) {
      console.warn('useAdyenCard: clientKey is required');
      return undefined;
    }

    const keyValidation = validateAdyenClientKey(clientKey, environment);
    if (!keyValidation.valid) {
      const msg = getAdyenClientKeySetupError(keyValidation.reason);
      setError(msg);
      onErrorRef.current?.(msg);
      return undefined;
    }

    if (!paymentMethodsResponse) {
      console.warn('useAdyenCard: paymentMethodsResponse is required');
      return undefined;
    }

    if (cardRef.current && mountedRef.current) {
      try {
        cardRef.current.unmount();
      } catch (err) {
        console.warn('Error during cleanup:', err);
      }
      cardRef.current = null;
      setCardComponent(null);
      setReady(false);
      setConfigReady(false);
      mountedRef.current = false;
    }

    let cancelled = false;

    const initializeAdyen = async () => {
      setLoading(true);
      setConfigReady(false);
      setReady(false);

      try {
        const cardMethods = cardOnlyAdyenPaymentMethods(paymentMethodsResponse)
          || paymentMethodsResponse;
        const schemeMethod = cardMethods.paymentMethods?.find(
          (pm) => pm?.type === 'scheme'
        );
        const brands = schemeMethod?.brands?.length
          ? schemeMethod.brands
          : cardConfiguration.brands;

        const checkout = await AdyenCheckout({
          clientKey,
          environment,
          paymentMethodsResponse: cardMethods,
          locale,
          countryCode,
          analytics: { enabled: false },
          onError: (err) => {
            if (isIgnorableAdyenError(err)) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('[Adyen] Ignoring non-blocking checkout error:', err);
              }
              return;
            }
            const errorMessage = err?.message || err?.error || 'Checkout error';
            setError(errorMessage);
            onErrorRef.current?.(errorMessage, err);
          },
          // v6 Advanced flow: pass actions through so /payments/details can
          // call actions.resolve({ resultCode, action }) instead of remounting.
          // https://docs.adyen.com/online-payments/3d-secure/native-3ds2
          onAdditionalDetails: (state, component, actions) => {
            onAdditionalDetailsRef.current?.(state.data, component, actions);
          },
          onPaymentCompleted: (result, component) => {
            onPaymentCompletedRef.current?.(result, component);
          },
          onPaymentFailed: (result, component) => {
            onPaymentFailedRef.current?.(result, component);
          },
          onSubmit: (state, component) => {
            resolveSubmit(state);
          },
        });

        if (cancelled) return;

        checkoutRef.current = checkout;
        setCheckoutInstance(checkout);

        const cardConfig = {
          brands,
          enableClickToPay: false,
          hasHolderName: true,
          holderNameRequired: true,
          positionHolderNameOnTop: true,
          showPayButton: false,
          showBrandsUnderCardNumber: true,
          showFormInstruction: false,
          // ACS window size advertised to the issuer. '05' fills the dedicated
          // 3DS container (100% × 100%). Default '02' is 390×400.
          challengeWindowSize: '05',
          styles,
          placeholders: {
            cardNumber: '1234 5678 9012 3456',
            expiryDate: 'MM/YY',
            securityCode: '123',
            holderName: 'J. Smith',
          },
          ...cardConfiguration,
          showWarnings: process.env.NODE_ENV === 'development',
          onReady: () => {
            setReady(true);
            onReadyRef.current?.();
          },
          onConfigSuccess: (data) => {
            setConfigReady(true);
            onConfigSuccessRef.current?.(data);
          },
          onBrand: (brandObject) => {
            setCardMetadata(brandObject);
            onBrandRef.current?.(brandObject);
          },
          onChange: (state, component) => {
            setError(null);
            onChangeRef.current?.(state, component);
          },
          onError: (err) => {
            if (isIgnorableAdyenError(err)) {
              try {
                cardRef.current?.setStatus?.('ready');
              } catch {
                // ignore
              }
              return;
            }
            const errorMessage = err?.message || err?.error || 'Card validation error';
            setError(errorMessage);
            onErrorRef.current?.(errorMessage, err);
          },
          onSubmit: (state, component) => {
            resolveSubmit(state);
          },
        };

        const card = new Card(checkout, cardConfig);
        if (cancelled) return;

        cardRef.current = card;
        setCardComponent(card);
        mountedRef.current = false;
      } catch (err) {
        if (!cancelled) {
          const errorMsg = err?.message || 'Failed to initialize Adyen';
          setError(errorMsg);
          onErrorRef.current?.(errorMsg, err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    initializeAdyen();

    return () => {
      cancelled = true;
      if (actionComponentRef.current) {
        try {
          actionComponentRef.current.unmount();
        } catch (err) {
          console.warn('Error unmounting 3DS action:', err);
        }
        actionComponentRef.current = null;
      }
      if (cardRef.current && mountedRef.current) {
        try {
          cardRef.current.unmount();
        } catch (err) {
          console.warn('Error unmounting card:', err);
        }
      }
      mountedRef.current = false;
      cardRef.current = null;
      checkoutRef.current = null;
    };
  }, [
    enabled,
    clientKey,
    environment,
    paymentMethodsResponse,
    locale,
    countryCode,
    resolveSubmit,
  ]);

  // Surface misconfigured client key / allowed origins when encryption never becomes ready
  useEffect(() => {
    if (!mountedRef.current || configReady || !enabled) {
      return undefined;
    }

    const timeoutMs = 15000;
    const timer = setTimeout(() => {
      if (!configReady && mountedRef.current) {
        const msg = getAdyenClientKeySetupError('config_timeout');
        setError(msg);
        onErrorRef.current?.(msg);
        if (process.env.NODE_ENV === 'development') {
          console.error('[Adyen] configReady timeout', {
            origin: window.location.origin,
            isSecureContext: window.isSecureContext,
            environment,
            clientKeyPrefix: clientKey?.slice(0, 10),
          });
        }
      }
    }, timeoutMs);

    return () => clearTimeout(timer);
  }, [configReady, enabled, clientKey, environment]);

  const mount = useCallback(async (target) => {
    if (!cardRef.current) {
      throw new Error('Card component not initialized');
    }
    // Claim the slot synchronously BEFORE any async work so a second
    // invocation (re-render / effect race) cannot mount twice and spawn
    // orphaned secured-field iframes ("data mismatch" warnings).
    if (mountedRef.current) {
      return;
    }
    mountedRef.current = true;

    const targetElement =
      typeof target === 'string' ? document.querySelector(target) : target;
    if (!targetElement || !targetElement.isConnected) {
      mountedRef.current = false;
      throw new Error('Card mount target not ready');
    }

    // Ensure the node is empty so we never stack a second set of iframes.
    while (targetElement.firstChild) {
      targetElement.removeChild(targetElement.firstChild);
    }

    try {
      cardRef.current.mount(targetElement);
    } catch (err) {
      mountedRef.current = false;
      throw err;
    }
  }, []);

  const unmount = useCallback(() => {
    if (!cardRef.current) return;
    try {
      cardRef.current.unmount();
      mountedRef.current = false;
      setConfigReady(false);
    } catch (err) {
      console.warn('Error unmounting card:', err);
    }
  }, []);

  const submit = useCallback(async () => {
    if (!cardRef.current) {
      throw new Error('Card component not initialized');
    }
    if (!configReady) {
      throw new Error('Card form is still loading. Please wait a moment.');
    }

    if (cardRef.current.isValid) {
      return {
        data: cardRef.current.data,
        isValid: true,
      };
    }

    const statePromise = new Promise((resolve, reject) => {
      submitResolverRef.current = resolve;
      setTimeout(() => {
        if (submitResolverRef.current) {
          submitResolverRef.current = null;
          if (cardRef.current?.isValid) {
            resolve({
              data: cardRef.current.data,
              isValid: true,
            });
          } else {
            reject(
              new Error(
                'Card validation failed. Check card number, expiry, CVC, and cardholder name.'
              )
            );
          }
        }
      }, 10000);
    });

    cardRef.current.submit();
    const state = await statePromise;

    if (!state?.isValid && !cardRef.current?.isValid) {
      throw new Error('Card validation failed');
    }

    return {
      data: state?.data || cardRef.current.data,
      isValid: true,
    };
  }, [configReady]);

  const handleAction = useCallback(async (action) => {
    if (!cardRef.current) {
      throw new Error('Card component not initialized');
    }
    return cardRef.current.handleAction(action);
  }, []);

  /**
   * Native 3DS2: mount the action in a dedicated container with an explicit
   * challenge window size. Subsequent steps must use actions.resolve — do not
   * call this again for the same shopper challenge.
   * https://docs.adyen.com/online-payments/3d-secure/native-3ds2
   */
  const createFromAction = useCallback((action, target, configuration = {}) => {
    if (!checkoutRef.current) {
      throw new Error('Checkout not initialized');
    }
    if (actionComponentRef.current) {
      try {
        actionComponentRef.current.unmount();
      } catch {
        // ignore
      }
      actionComponentRef.current = null;
    }

    const targetElement =
      typeof target === 'string' ? document.querySelector(target) : target;
    if (!targetElement || !targetElement.isConnected) {
      throw new Error('3DS mount target not ready');
    }

    while (targetElement.firstChild) {
      targetElement.removeChild(targetElement.firstChild);
    }

    const component = checkoutRef.current.createFromAction(action, {
      challengeWindowSize: '05',
      ...configuration,
    });
    actionComponentRef.current = component;
    component.mount(targetElement);
    return component;
  }, []);

  const isValid = useCallback(() => {
    return Boolean(cardRef.current?.isValid);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    checkoutInstance,
    cardComponent,
    ready,
    configReady,
    error,
    loading,
    cardMetadata,
    mount,
    unmount,
    submit,
    handleAction,
    createFromAction,
    isValid,
    clearError,
  };
};

export default useAdyenCard;
